import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import { FormsModule, FormGroup } from '@angular/forms';
import { QaiButtonComponent, QaiDialogComponent } from '@qai/ui';
import { AssignmentInformationViewComponent } from '../../assignment-information-view/assignment-information-view.component';
import { AssignmentReactiveFormService } from 'src/app/pages/self-serve/simulation-builder/services/assignment-reactive-form.service';
import { AssignmentTypeEnum } from 'src/app/utils/enums/self-serve/assignment-type.enum';
import { TRANSLOCO_SCOPE, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { take } from 'rxjs';

@Component({
  selector: 'assignment-modal',
  standalone: true,
  templateUrl: './assignment-modal.component.html',
  styleUrls: ['./assignment-modal.component.scss'],
  imports: [
    FormsModule,
    CommonModule,
    QaiDialogComponent,
    QaiButtonComponent,
    AssignmentInformationViewComponent,
    TranslocoModule,
  ],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class AssignmentModalComponent implements OnInit, OnDestroy {
  @ViewChild('dialog') qaiDialogRef: QaiDialogComponent | undefined;

  tempUuid: string = '';

  @Input() open: boolean = false;
  @Input() networkRequestInProgress: boolean = false;
  @Input() moduleAssignmentUuid: string | null = null;

  @Output() assignmentSave = new EventEmitter<any>();
  @Output() handleSaveAndClose = new EventEmitter<any>();
  @Output() closeAssignmentModal = new EventEmitter<any>();

  form: FormGroup | null = null;
  assignmentTypeOptions: { label: string; value: AssignmentTypeEnum }[] = [];

  constructor(
    private assignmentFormService: AssignmentReactiveFormService,
    private translocoService: TranslocoService
  ) {}

  ngOnInit(): void {
    this.translocoService.selectTranslation('simulationBuilder').pipe(take(1)).subscribe(() => {
      this.assignmentTypeOptions = [
        { label: this.translocoService.translate('simulationBuilder.ASSIGNMENT_TYPE_CERTIFICATION'), value: AssignmentTypeEnum.CERTIFICATION },
        { label: this.translocoService.translate('simulationBuilder.ASSIGNMENT_TYPE_PRACTICE'), value: AssignmentTypeEnum.PRACTICE },
      ];
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']) {
      const isOpenNow = changes['open']?.currentValue;
      if (isOpenNow) {
        this.openModal();
        this.initForm();
      } else {
        if (this.qaiDialogRef) {
          this.qaiDialogRef.close();
        }
      }
    }

    if (changes['open'] && changes['open'].currentValue && !changes['open'].previousValue) {
      this.assignmentFormService.setAssignmentNameConflict(false);
    }
  }

  ngOnDestroy(): void {
    this.destroyForm();
  }

  initForm() {
    if (this.moduleAssignmentUuid) {
      this.assignmentFormService
        .getForm(this.moduleAssignmentUuid)
        .subscribe((form: FormGroup) => {
          this.form = form.get('details') as FormGroup;
        });
    } else {
      this.tempUuid = Date.now().toString();
      this.assignmentFormService.createAssignment({}, this.tempUuid);
      this.assignmentFormService
        .getForm(this.tempUuid)
        .subscribe((form: FormGroup) => {
          this.form = form.get('details') as FormGroup;
        });
    }
  }

  getModuleAssignmentUuid() {
    return this.moduleAssignmentUuid || this.tempUuid;
  }

  getModuleUuid(): string {
    return this.form?.get('moduleUuid')?.value || '';
  }

  handleSuccessClick() {
    if (this.form && this.form.valid) {
      this.assignmentSave.emit(this.form.value);
      this.destroyForm();
    }
  }

  handleSaveAndCloseClick() {
    if (this.form && this.form.valid) {
      this.handleSaveAndClose.emit(this.form.value);
      this.destroyForm();
    }
  }

  handleCloseModal() {
    this.closeAssignmentModal.emit();
  }

  closeModal() {
    this.destroyForm();
    if (this.qaiDialogRef) {
      this.qaiDialogRef.close();
    }
  }

  openModal() {
    if (this.qaiDialogRef) {
      this.qaiDialogRef.showModal();
    }
  }

  destroyForm() {
    this.assignmentFormService.deleteAssignment(this.tempUuid);
    this.assignmentFormService.setAssignmentNameConflict(false);
  }
}
